/**
 * 法定ページ 6 本の**雛形と差し込みの器**（R0 §2.1）。
 *
 * ここが唯一の正になるのは次の 3 つ:
 *   1. 文書の版番号（`LEGAL_DOCUMENT_VERSIONS`）— `consents.document_version` と共有する
 *   2. 文面そのもの（`renderLegalDocument`）
 *   3. 「事業者固有の値が埋まっていない文書は公開しない」判定（`missingLegalFields`）
 *
 * **なぜ雛形をコードに置くのか。** 法定ページは「商号・住所・代表者名・電話番号・
 * 適格請求書発行事業者登録番号」といった**人間しか用意できない値**と、
 * 「メールホスティング事業として書かねばならない条項」の 2 層でできている。
 * 後者は実装で閉じられるので閉じ、前者は env から差し込む器だけを作る。
 * 値が入っていない文書は `published: false` になり、画面には「準備中」が出る。
 * **架空の商号や住所を既定値として埋めない。** 埋めると「準備中」が出ないまま
 * 嘘の表記が公開され、特商法 11 条違反が「実装済み」に見えてしまう。
 *
 * **なぜハッシュを取るのか。** `consents.document_hash` に「同意した瞬間に
 * 実際に表示していた文面」の sha256 を残すため。版番号だけだと、後から
 * 同じ版番号のまま文面を差し替えられてしまい「何に同意したか」を証明できない。
 * ハッシュ計算そのもの（node:crypto）はここでは行わない。この package は
 * SPA からも import されるので、Node 固有 API を持ち込むと Vite のビルドが壊れる。
 * `legalDocumentText()` が返す**正規化テキスト**を呼び出し側でハッシュする。
 *
 * **士業レビュー前であることの明示**は `profile.draft` で制御する。
 * true のあいだは各ページ冒頭に警告ブロックが入る。レビューが済んだら
 * `LEGAL_DRAFT_NOTICE=0` にして落とす（文面が変わるのでハッシュも変わる＝
 * 「レビュー前の版に同意した人」と「レビュー後の版に同意した人」が区別できる）。
 */
import {
  DEFAULT_SLA_TIERS,
  PLANNED_MAINTENANCE_MONTHLY_MINUTES_MAX,
  PLANNED_MAINTENANCE_NOTICE_DAYS,
  SLA_UPTIME_GUARANTEE_BP,
} from './billing-math.js';
import { PLAN_CATALOG, overagePriceLine, overagePriceRows } from './plans.js';

/* ------------------------------------------------------------------ 文書の識別 */

/** 法定ページの slug。URL（`/legal/<slug>`）とファイル名に一致させる。 */
export const LEGAL_DOCUMENT_SLUGS = [
  'terms',
  'privacy',
  'aup',
  'sla',
  'tokushoho',
  'subcontractors',
  /*
   * 契約者データへのアクセス手続（break-glass）。
   *
   * 利用規約 第 7 条が「別紙に定める手続」を参照しているのに、その別紙に
   * 対応する公開文書が存在しなかった。**掲示している統制を契約者が読めない**
   * 状態は、監査・大口顧客のセキュリティ質問票で必ず指摘される。
   * 文面は `apps/api/src/routes/ops/break-glass.ts` の実装
   * （申請理由の記録 → 別担当者の承認 → 時限付与 → 事後の契約者通知）を
   * そのまま条文化してある。**実装を変えたらこの文書も変える。**
   */
  'data_access',
] as const;

export type LegalDocumentSlug = (typeof LEGAL_DOCUMENT_SLUGS)[number];

export const LEGAL_DOCUMENT_TITLES_JA: Readonly<Record<LegalDocumentSlug, string>> = {
  terms: '利用規約',
  privacy: 'プライバシーポリシー',
  aup: '禁止事項（Acceptable Use Policy）',
  sla: 'サービス品質目標（SLA）',
  tokushoho: '特定商取引法に基づく表記',
  subcontractors: '委託先一覧',
  data_access: '契約者データへのアクセス手続',
};

/**
 * 文書の版。**文面を 1 文字でも変えたら必ず上げる。**
 *
 * `consents.document_version` に入る値なので、`yyyy-MM-dd` 形式で固定する
 * （`lib/time.ts` の `yyyyMM` とは別系統。こちらは人間が読む版番号）。
 */
export const LEGAL_DOCUMENT_VERSIONS: Readonly<Record<LegalDocumentSlug, string>> = {
  // 2026-08-23: 料金条項を実装（期中の日割り按分）に合わせて改め、
  //             販売価格欄に総額表示と超過料金の掲示を明記した。
  //             **文面を変えたら必ず版を上げる。** 版を据え置くと、
  //             同じ版番号で別の文面に同意させたことになり `consents` の
  //             document_hash と突き合わせたときに証明が崩れる。
  // 2026-08-28: (1) 第 7 条 2 項の「本文にアクセスしません」を実装に合わせて
  //             「人による閲覧」に限定し、提供に必要な自動処理（迷惑メール判定・
  //             受信者保護のための画像の代理取得）を別項に分けた。
  //             (2) Webメールの AI 機能について、本文または抜粋を委託先の
  //             LLM へ送信することを明記（第 7 条 4〜6 項）。
  //             (3) 第 6 条 3 項の全部免責を第 9 条の責任制限へ一本化した
  //             （消費者契約法 8 条 1 項に耐えない条項だった）。
  //             (4) 未成年者の申込（第 3 条 4 項）と反社会的勢力の排除
  //             （第 11 条の 2）を追加。(5) 個人データの取扱いの委託
  //             （第 7 条の 2）を新設。(6) 無料試用期間の日数と年額前払いの
  //             取扱いを第 4 条に明記。(7) 第 11 条に通知の方法と、効力発生日
  //             までに同意しなかった場合の扱い（解約の申出／継続利用による
  //             みなし同意）を追加した。**この版はまだ効力が発生していない**
  //             （`LEGAL_DOCUMENT_SCHEDULE.terms.effectiveFrom` = 2026-09-27）
  //             ので、周知日と同日の文面追記に留めて版は据え置いた。
  //             **効力発生後に文面を変えるときは必ず版を上げること。**
  //             **文面を変えたら必ず版を上げる。** 版を据え置くと、
  //             同じ版番号で別の文面に同意させたことになり `consents` の
  //             document_hash と突き合わせたときに証明が崩れる。
  terms: '2026-08-28',
  // 2026-08-28: (1) AI 機能で本文・抜粋を委託先 LLM へ送ることと越境移転
  //             （法 28 条）を §1・§3・§4 に明記。(2) 画像プロキシの記録を
  //             取得情報表に追加。(3) 保存期間表に配送ログの実日数・認証記録・
  //             解約後の但し書きを追加。(4) 安全管理措置の「保存データの暗号化」を
  //             実装（バックアップと認証情報・鍵の暗号化）に合わせて正した。
  //             (5) 事業者表に代表者を追加し、必須項目にも入れた（法 32 条 1 項）。
  privacy: '2026-08-28',
  aup: '2026-08-01',
  // 2026-08-28: サポートの受付時間を実装（`supportSlaFor()` / `SUPPORT_HOURS` の
  //             平日 9:00〜18:00）に合わせ、一次回答の目安をプラン別に掲示した。
  sla: '2026-08-28',
  // 2026-08-28: (1) 無料試用期間（14 日）と課金開始日を販売価格欄に明記。
  //             (2) 年額前払いの支払時期と中途解約時の取扱いを追加。
  //             (3) 返品・キャンセル欄の自己矛盾（「日割り按分」と
  //             「前払金の返金は行いません」の同居）を実装に合わせて解消した。
  tokushoho: '2026-08-28',
  subcontractors: '2026-08-01',
  data_access: '2026-08-28',
};

/** 利用規約の現行版。`consents` と signup の zod が参照する。 */
export const TERMS_VERSION = LEGAL_DOCUMENT_VERSIONS.terms;
/** プライバシーポリシーの現行版。 */
export const PRIVACY_VERSION = LEGAL_DOCUMENT_VERSIONS.privacy;

/* ------------------------------------------------------- 改定の周知と効力発生 */

export interface LegalVersionSchedule {
  /** 改定を周知した日（`yyyy-MM-dd`、JST）。**掲示を差し替えた日**を書く。 */
  announcedOn: string;
  /**
   * 効力発生日（`yyyy-MM-dd`、JST）。この日が来るまで、**既存の契約者には
   * 前版が適用される**（新規申込は掲示している現行版に同意して契約する）。
   */
  effectiveFrom: string;
  /**
   * 契約者に不利益となりうる変更か。true の版は
   * 「`announcedOn` + `PRICE_CHANGE_NOTICE_DAYS` <= `effectiveFrom`」を満たす
   * 必要がある（利用規約 第 11 条 2 項で当社がそう掲示しているため）。
   */
  adverse: boolean;
  /**
   * この版が置き換える直前の版。`effectiveFrom` までは**こちらが現行**。
   * 初版なら null（＝掲示した瞬間から現行）。
   */
  supersedes: string | null;
}

/**
 * 版ごとの周知日・効力発生日。**`LEGAL_DOCUMENT_VERSIONS` を上げたら必ず更新する。**
 *
 * **なぜ要るのか。** 版番号を上げるだけの実装だと、デプロイした瞬間に
 * 全契約者が「未同意」に変わり、ポータルに再同意バナーが出る。ところが
 * 利用規約 第 11 条 2 項は「不利益となる変更は効力発生日の 30 日前までに
 * 通知する」と掲示しているので、**掲示した手続を当社が一度も踏まないまま
 * 新版を適用したことになる**（民法 548 条の 4 の要件を欠く＝改定後の条項を
 * 根拠に停止も免責も主張できない）。
 *
 * ここで効力発生日を持たせ、`effectiveLegalVersion()` が
 * 「いま実際に適用されている版」を返すことで、
 *   - 既存契約者は効力発生日までは前版のままで未同意にならない
 *   - 効力発生日までのあいだ、画面と API は「いつから何が変わるか」を出せる
 *   - 30 日を割った改定はテストが機械的に落とす
 * の 3 つが同時に成り立つ。
 *
 * **効力発生日は同意対象の文面ではない**ので `legalDocumentText()`
 * （＝ハッシュ対象）には**入れない**。入れると、日付の訂正だけで
 * 既存の同意記録のハッシュが全部合わなくなる。
 */
export const LEGAL_DOCUMENT_SCHEDULE: Readonly<Record<LegalDocumentSlug, LegalVersionSchedule>> = {
  /*
   * 2026-08-28 の改定は、AI 機能で本文（抜粋または全体）を委託先 LLM へ
   * 送信することの明記を含む。**契約者から見れば取扱いの追加**なので
   * adverse として扱い、周知から 30 日後を効力発生日にしてある。
   * （実際には既定で無効・契約者が有効化したときだけ動く機能だが、
   *   「不利益ではない」と当社が自己判断して即時適用する形にはしない。）
   */
  terms: {
    announcedOn: '2026-08-28',
    effectiveFrom: '2026-09-27',
    adverse: true,
    supersedes: '2026-08-23',
  },
  privacy: {
    announcedOn: '2026-08-28',
    effectiveFrom: '2026-09-27',
    adverse: true,
    supersedes: '2026-08-23',
  },
  aup: {
    announcedOn: '2026-08-01',
    effectiveFrom: '2026-08-01',
    adverse: false,
    supersedes: null,
  },
  // SLA・特商法・委託先一覧・アクセス手続は同意対象ではない（`consentKindForSlug`
  // が null を返す）ので、掲示の差し替えがそのまま効力発生になる。
  sla: {
    announcedOn: '2026-08-28',
    effectiveFrom: '2026-08-28',
    adverse: false,
    supersedes: '2026-08-23',
  },
  tokushoho: {
    announcedOn: '2026-08-28',
    effectiveFrom: '2026-08-28',
    adverse: false,
    supersedes: '2026-08-23',
  },
  subcontractors: {
    announcedOn: '2026-08-01',
    effectiveFrom: '2026-08-01',
    adverse: false,
    supersedes: null,
  },
  data_access: {
    announcedOn: '2026-08-28',
    effectiveFrom: '2026-08-28',
    adverse: false,
    supersedes: null,
  },
};

/** `yyyy-MM-dd`（JST の暦日）を、その日の 00:00 JST の Date にする。 */
function jstDay(day: string): Date {
  // JST 固定。`lib/time.ts` は shared から見えないので、ここでは
  // オフセット付きの ISO 文字列を作って Date に解釈させる（DST が無いので安全）。
  return new Date(`${day}T00:00:00+09:00`);
}

/** 周知日から効力発生日までの日数。 */
export function legalNoticeDays(schedule: LegalVersionSchedule): number {
  const ms = jstDay(schedule.effectiveFrom).getTime() - jstDay(schedule.announcedOn).getTime();
  return Math.round(ms / 86_400_000);
}

/**
 * その時点で**実際に適用されている**版。
 *
 * 効力発生日が未到来なら `supersedes`（前版）を返す。前版が無い（初版）なら
 * 掲示した版をそのまま返す。既存契約者の同意判定はこの値で行う。
 */
export function effectiveLegalVersion(slug: LegalDocumentSlug, at: Date = new Date()): string {
  const schedule = LEGAL_DOCUMENT_SCHEDULE[slug];
  const published = LEGAL_DOCUMENT_VERSIONS[slug];
  if (at.getTime() >= jstDay(schedule.effectiveFrom).getTime()) return published;
  return schedule.supersedes ?? published;
}

/** 掲示済みだが、まだ効力が発生していない版か。 */
export function isLegalVersionScheduled(slug: LegalDocumentSlug, at: Date = new Date()): boolean {
  return effectiveLegalVersion(slug, at) !== LEGAL_DOCUMENT_VERSIONS[slug];
}

export interface ScheduledLegalChange {
  slug: LegalDocumentSlug;
  title: string;
  /** これから適用される版。 */
  version: string;
  /** それまで適用され続ける版。 */
  currentVersion: string;
  effectiveFrom: string;
  announcedOn: string;
  adverse: boolean;
}

/** これから効力が発生する改定の一覧（画面の事前告知に使う）。 */
export function scheduledLegalChanges(at: Date = new Date()): ScheduledLegalChange[] {
  const out: ScheduledLegalChange[] = [];
  for (const slug of LEGAL_DOCUMENT_SLUGS) {
    if (!isLegalVersionScheduled(slug, at)) continue;
    const schedule = LEGAL_DOCUMENT_SCHEDULE[slug];
    out.push({
      slug,
      title: LEGAL_DOCUMENT_TITLES_JA[slug],
      version: LEGAL_DOCUMENT_VERSIONS[slug],
      currentVersion: effectiveLegalVersion(slug, at),
      effectiveFrom: schedule.effectiveFrom,
      announcedOn: schedule.announcedOn,
      adverse: schedule.adverse,
    });
  }
  return out;
}

/**
 * 掲示している周知期間を満たしていない改定の一覧。**空でなければ掲示が嘘。**
 *
 * 起動時検査とテストの両方から引く。`adverse` な改定は
 * `PRICE_CHANGE_NOTICE_DAYS`（30 日）以上の周知が要る。
 */
export function legalNoticeViolations(): string[] {
  const out: string[] = [];
  for (const slug of LEGAL_DOCUMENT_SLUGS) {
    const schedule = LEGAL_DOCUMENT_SCHEDULE[slug];
    if (!schedule.adverse) continue;
    const days = legalNoticeDays(schedule);
    if (days < PRICE_CHANGE_NOTICE_DAYS) {
      out.push(
        `${LEGAL_DOCUMENT_TITLES_JA[slug]}（${LEGAL_DOCUMENT_VERSIONS[slug]} 版）の周知期間が ` +
          `${days} 日しかありません。不利益変更は ${PRICE_CHANGE_NOTICE_DAYS} 日以上必要です。`,
      );
    }
    if (schedule.supersedes === null) {
      out.push(
        `${LEGAL_DOCUMENT_TITLES_JA[slug]}（${LEGAL_DOCUMENT_VERSIONS[slug]} 版）は ` +
          `不利益変更ですが、置き換える前版（supersedes）が指定されていません。`,
      );
    }
  }
  return out;
}

/**
 * 不利益変更（値上げを含む）の周知から効力発生までに置く日数。
 *
 * **掲示（利用規約 第 11 条）とコードの正を 1 つにするための定数。**
 * 民法 548 条の 4 は「効力発生時期を定め、その時期が到来するまでに周知する」
 * ことを求めており、掲示ではそれを 30 日前と具体化している。
 * 運営 API（`POST /ops/billing/subscriptions/:id/reprice`）はこの日数より
 * 手前の適用日を受け付けない。掲示だけ直して実装が古いままだと、
 * 掲示した手続きを当社が守っていないことになるので、両方をここから引く。
 */
export const PRICE_CHANGE_NOTICE_DAYS = 30;

/**
 * 無料試用期間の日数。
 *
 * **掲示（LP・申込画面・特商法表記・利用規約）と実装（`POST /public/signup` が
 * `trialEndsAt` を作るときの日数）の正を 1 つにするための定数。**
 * 「無料期間の長さ」は最も効く獲得材料であると同時に、期間の表示と終了前の
 * 通知が無いまま有料契約へ自動転換すると、特商法の申込画面表示・
 * 消費者契約法の観点で問題になる。掲示だけ直して実装が別の日数のままだと
 * 「15 日目から課金」と書いた翌日に課金が始まる、という形で表面化する。
 */
export const TRIAL_PERIOD_DAYS = 14;

/**
 * 配送ログ（`mail_log_entries`）の保持日数。
 *
 * **プライバシーポリシーの保存期間表と `maillog.retention` の実装で共有する。**
 * 掲示は 90 日・実装は 180 日という食い違いがあり、掲示のほうが短かった
 * （＝「90 日で消えます」と読んだ契約者の期待より 3 か月長く残っていた）。
 * 値は実装側（180）に合わせてある。**短くするなら
 * `apps/worker/src/workers/usage-rollup.ts` の削除ジョブを先に変えること。**
 * 掲示だけ短くすると、掲示のほうが嘘になる。
 */
export const MAIL_LOG_RETENTION_DAYS = 180;

/**
 * 認証記録（`node_auth_events`。IMAP/SMTP の認証成否）の保持日数。
 *
 * 実装は `apps/worker/src/workers/maintenance.ts` の `AUTH_EVENT_RETENTION_DAYS`。
 * 掲示（保存期間表）に 1 行も無かったので、読者は「監査ログと同じ 7 年」と
 * 誤解するか、記録が存在しないと誤解するかのどちらかになっていた。
 */
export const AUTH_EVENT_RETENTION_DAYS = 365;

/**
 * 本人からの権利行使のご請求（開示・訂正・利用停止・消去・可搬・第三者提供記録）の
 * 対応期限（受付から起算する日数）。
 *
 * 実装の正は `apps/api/src/routes/ops/support.ts` の `DSR_DUE_DAYS`
 * （`data_subject_requests.due_at` を受付時に確定させる）。**この日数が
 * 掲示に一切書かれていなかった。** 個人情報保護法は「遅滞なく」としか
 * 定めておらず、事業者が自ら期限を具体化しなければ請求者は待つ目安を
 * 持てない。実装側は 14 日で期限超過を検出して一覧に出すのに、
 * 掲示が無いので「何日で回答すると約束したのか」を示せなかった。
 */
export const DATA_SUBJECT_REQUEST_DUE_DAYS = 14;

/**
 * サポートの受付時間（日本時間）。**SLA ページの掲示と期限計算で共有する値。**
 *
 * 実装の正は `apps/api/src/services/support.ts` の `SUPPORT_HOURS`
 * （`addBusinessMinutes()` が営業時間を進める窓）。掲示は 10:00〜18:00、
 * 実装は 9:00〜18:00 で 1 時間ずれており、9:15 に届いたチケットが
 * 「掲示上はまだ受付前なのに実装上は営業時間内」という状態になっていた。
 * **掲示を実装に合わせてある。**
 */
export const SUPPORT_BUSINESS_HOURS = { startHour: 9, endHour: 18 } as const;

/**
 * 一次回答の目標時間（営業時間の分数）。`supportSlaFor()` の値と一致させる。
 *
 * 「優先サポート」を上位プランの差別化として販売しているのに、その中身
 * （何時間で一次回答するのか・受付時間はいつか）が契約前に読める場所に
 * 一切書かれていなかった。買う前に比べられないものは差別化として機能せず、
 * 運営の側も「何を約束していたか」を示せない。
 */
export const SUPPORT_FIRST_RESPONSE_MINUTES = {
  /** `features.prioritySupport` のプラン。 */
  priority: 4 * 60,
  /** 標準サポート。1 営業日 =（受付時間 9 時間）として数える。 */
  standard: 9 * 60,
} as const;

/**
 * サインアップで**必ず**同意を取る文書。
 *
 * `consent_kind` enum（`terms` / `privacy` / `dpa` / `marketing`）の値に一致させる。
 * AUP は利用規約に「別紙として一体をなす」と書いて取り込む（enum に `aup` が無いのと、
 * 実務上も AUP は規約の一部として扱うのが普通）。**AUP の版番号は
 * 利用規約の本文に明記する**ので、AUP だけ差し替えて同意を回避することはできない。
 */
export const SIGNUP_CONSENT_KINDS = ['terms', 'privacy'] as const;
export type SignupConsentKind = (typeof SIGNUP_CONSENT_KINDS)[number];

/** slug → 同意種別。`sla` などは同意対象ではないので undefined。 */
export function consentKindForSlug(slug: LegalDocumentSlug): SignupConsentKind | null {
  if (slug === 'terms') return 'terms';
  if (slug === 'privacy') return 'privacy';
  return null;
}

/* ------------------------------------------------------------ 事業者固有の値 */

export interface LegalSubcontractor {
  /** 委託先の名称。 */
  name: string;
  /** 委託する業務の内容。 */
  role: string;
  /** 取扱いが行われる国・地域（越境移転の明示に要る）。 */
  region: string;
}

/**
 * 法定ページに差し込む事業者固有の値。**空文字は「未設定」を意味する。**
 *
 * 既定値を持たせないのは意図的。`companyName` に「株式会社サンプル」のような
 * 値を入れておくと、設定漏れのまま公開されても誰も気付かない。
 */
export interface LegalIssuerProfile {
  /* --- サービス側（env から自動で埋まる） --- */
  serviceName: string;
  serviceBaseDomain: string;
  abuseEmail: string;
  supportEmail: string;
  /* --- 事業者（人間が用意する。未設定なら公開しない） --- */
  companyName: string;
  representativeName: string;
  postalCode: string;
  address: string;
  phone: string;
  contactEmail: string;
  privacyContactEmail: string;
  /** 適格請求書発行事業者登録番号（T + 13 桁）。 */
  invoiceRegistrationNo: string;
  /** 電気通信事業の届出番号（法 16 条）。届出受理まで空。 */
  telecomNotificationNo: string;
  /** 個人情報保護管理者の職名など。空なら「個人情報保護管理者」とだけ書く。 */
  privacyOfficer: string;
  /** 委託先一覧。空配列なら `/legal/subcontractors` は公開しない。 */
  subcontractors: readonly LegalSubcontractor[];
  /** SLA のクレジット条項を掲示するか。**測れるようになるまで false。** */
  slaCreditsEnabled: boolean;
  /** 士業レビュー前の注記を出すか。 */
  draft: boolean;
}

/** 全項目が空の profile。テストと「何も設定されていない」状態の表現に使う。 */
export function emptyLegalProfile(): LegalIssuerProfile {
  return {
    serviceName: '',
    serviceBaseDomain: '',
    abuseEmail: '',
    supportEmail: '',
    companyName: '',
    representativeName: '',
    postalCode: '',
    address: '',
    phone: '',
    contactEmail: '',
    privacyContactEmail: '',
    invoiceRegistrationNo: '',
    telecomNotificationNo: '',
    privacyOfficer: '',
    subcontractors: [],
    slaCreditsEnabled: false,
    draft: true,
  };
}

/**
 * 文書ごとの必須項目。**ここが埋まっていない文書は公開しない。**
 *
 * 特商法（11 条）が最も要求が多く、事業者名・住所・電話番号・代表者名が揃わないと
 * 「表記した」ことにならない。逆に AUP は連絡先さえあれば機能する。
 */
export const LEGAL_REQUIRED_FIELDS: Readonly<
  Record<LegalDocumentSlug, readonly (keyof LegalIssuerProfile)[]>
> = {
  terms: ['companyName', 'address', 'contactEmail'],
  /*
   * 個人情報保護法 32 条 1 項 1 号・施行令 10 条は、保有個人データに関する
   * 公表事項として「氏名または名称、住所、**法人の場合は代表者の氏名**」を
   * 求めている。`representativeName` が空でもプライバシーポリシーが公開できると、
   * 公表事項を 1 つ欠いたまま運用が始まる。
   */
  privacy: ['companyName', 'representativeName', 'address', 'privacyContactEmail'],
  aup: ['companyName', 'abuseEmail'],
  sla: ['companyName'],
  // 別紙（アクセス手続）は「誰に問い合わせればよいか」が書ければ機能する。
  data_access: ['companyName', 'privacyContactEmail'],
  // 特商法 11 条: 事業者名 / 住所 / 電話番号 / 代表者（または業務責任者）/ 連絡先。
  tokushoho: [
    'companyName',
    'representativeName',
    'postalCode',
    'address',
    'phone',
    'contactEmail',
  ],
  subcontractors: ['companyName', 'subcontractors'],
};

/** 未設定の必須項目名を返す。空配列なら公開できる。 */
export function missingLegalFields(
  slug: LegalDocumentSlug,
  profile: LegalIssuerProfile,
): (keyof LegalIssuerProfile)[] {
  const missing: (keyof LegalIssuerProfile)[] = [];
  for (const key of LEGAL_REQUIRED_FIELDS[slug]) {
    const value = profile[key];
    if (Array.isArray(value)) {
      if (value.length === 0) missing.push(key);
    } else if (typeof value === 'string') {
      if (value.trim().length === 0) missing.push(key);
    }
  }
  return missing;
}

/**
 * **実際に使っている委託先が「委託先一覧」に載っているか**を検査する。
 *
 * 委託先一覧は env の自由記述 1 本（`LEGAL_SUBCONTRACTORS`）で、これまで
 * 書式の不正しか検査していなかった。決済代行を有効にしても、共有の AI
 * プロバイダを設定しても、掲示は空のまま起動できてしまう。
 * 個人情報保護法 25 条（委託先の監督）の前提になる台帳がここしか無いのに、
 * **実態と掲示がずれたことを誰も検出しない**状態だった。しかも
 * プライバシーポリシー §4 は「委託先一覧に掲載しています」と参照し続ける。
 *
 * 判定は名称・業務内容の**部分一致**に留める（事業者名の表記ゆれまでは
 * 追えないので、厳密に照合しようとすると誤検知で無視される警告になる）。
 * 返すのは警告の文言だけで、起動を止めるかどうかは呼び出し側が決める。
 */
export function subcontractorCoverageWarnings(
  profile: LegalIssuerProfile,
  usage: {
    /** 決済代行を使っているか（`BILLING_PROVIDER=stripe` など）。 */
    paymentProvider: string | null;
    /** 共有の AI プロバイダ（`none` / 空なら使っていない）。 */
    aiProvider: string | null;
    /** オブジェクトストレージの事業者名（分かる範囲で。空なら検査しない）。 */
    objectStorage?: string | null;
  },
): string[] {
  const out: string[] = [];
  const haystack = profile.subcontractors
    .map((s) => `${s.name} ${s.role} ${s.region}`.toLowerCase())
    .join(' | ');
  const mentions = (...needles: string[]): boolean =>
    needles.some((n) => n.length > 0 && haystack.includes(n.toLowerCase()));

  const payment = (usage.paymentProvider ?? '').trim();
  if (payment.length > 0 && payment !== 'manual' && !mentions(payment, '決済', '課金')) {
    out.push(
      `決済代行（${payment}）を使う設定ですが、委託先一覧に決済の行がありません。` +
        'LEGAL_SUBCONTRACTORS に追加してください（個人情報保護法 25 条）。',
    );
  }

  const ai = (usage.aiProvider ?? '').trim();
  if (ai.length > 0 && ai !== 'none' && !mentions(ai, 'ai', 'llm', '言語モデル')) {
    out.push(
      `共有の AI プロバイダ（${ai}）を使う設定ですが、委託先一覧に AI の行がありません。` +
        'プライバシーポリシーは「委託先一覧に掲載しています」と参照しているため、' +
        '載せないと掲示が空振りします。',
    );
  }

  const storage = (usage.objectStorage ?? '').trim();
  if (storage.length > 0 && !mentions(storage, 'ストレージ', 'storage')) {
    out.push(
      `オブジェクトストレージ（${storage}）が委託先一覧にありません。` +
        '契約者の配信本文・アセット・請求書 PDF の保管先です。',
    );
  }

  /*
   * **プライバシーポリシーは委託先一覧が空でも公開できる**（必須項目に
   * 入っていない）。委託先一覧のほうは空だと公開されないので、
   * 「掲載しています」と書いたページから辿った先が『準備中』になる。
   */
  if (profile.subcontractors.length === 0) {
    out.push(
      '委託先一覧が空です。プライバシーポリシーは「委託先一覧に掲載しています」と' +
        '参照していますが、委託先一覧のページは公開されません（準備中の表示になります）。',
    );
  }
  return out;
}

/** その文書を公開してよいか。 */
export function isLegalDocumentPublishable(
  slug: LegalDocumentSlug,
  profile: LegalIssuerProfile,
): boolean {
  return missingLegalFields(slug, profile).length === 0;
}

/**
 * **サインアップを受け付けてよいか。**
 *
 * 利用規約とプライバシーポリシーが公開できない状態で申込を受けると、
 * 「同意した文面」が存在しないまま契約が成立する。`consents` に行を作れても
 * 中身が空なので証跡にならない。したがって器が埋まるまで申込ごと止める。
 */
export function isSignupLegallyReady(profile: LegalIssuerProfile): boolean {
  return SIGNUP_REQUIRED_DOCUMENTS.every((slug) => isLegalDocumentPublishable(slug, profile));
}

/**
 * **申込を受け付けてよいことの条件になる文書。**
 *
 * 同意を取る 2 本（規約・プライバシー）に加えて**特商法 11 条の表記**を含める。
 * 通信販売にあたる申込を受ける以上、事業者名・住所・電話番号・代表者名・
 * 販売価格・解約条件の表示は**申込画面から到達できる状態でなければならない**。
 * 同意を取らない文書だからといって申込ゲートから外すと、
 * 「特商法ページが準備中のまま課金が始まっている」という、
 * 表記義務違反そのものの状態でセルフサインアップが動いてしまう。
 */
export const SIGNUP_REQUIRED_DOCUMENTS = ['terms', 'privacy', 'tokushoho'] as const satisfies
  readonly LegalDocumentSlug[];

/** 申込ゲートで欠けている文書の slug を返す（運営が何を埋めればよいか分かる形）。 */
export function missingSignupDocuments(profile: LegalIssuerProfile): LegalDocumentSlug[] {
  return SIGNUP_REQUIRED_DOCUMENTS.filter((slug) => !isLegalDocumentPublishable(slug, profile));
}

/* ------------------------------------------------------------------ 文書の構造 */

export type LegalBlock =
  /** 条見出し。`number` は「第 1 条」など。 */
  | { kind: 'section'; number: string; heading: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: readonly string[] }
  /** 特商法の表・委託先一覧のような「項目名 : 値」の並び。 */
  | { kind: 'table'; rows: readonly { label: string; value: string }[] }
  /** 目立たせる注記。`tone` は画面側の色分けにだけ使う。 */
  | { kind: 'notice'; tone: 'warning' | 'info'; text: string };

export interface LegalDocument {
  slug: LegalDocumentSlug;
  title: string;
  version: string;
  /** 士業レビュー前か。true のとき先頭に警告ブロックが入っている。 */
  draft: boolean;
  /**
   * この版の効力発生日（`yyyy-MM-dd`）。**`legalDocumentText()` には含めない。**
   *
   * 効力発生日は「何に同意したか」ではなく「いつから適用されるか」なので、
   * ハッシュ対象に混ぜると、日付を 1 日訂正しただけで既存の同意記録の
   * ハッシュが全部合わなくなる（＝証跡として使えなくなる）。
   */
  effectiveFrom: string;
  /**
   * 効力発生日が未到来のあいだ、既存契約者に適用され続ける版。
   * 未到来でなければ `version` と同じ。
   */
  currentVersion: string;
  blocks: readonly LegalBlock[];
}

const DRAFT_NOTICE =
  'この文面は雛形です。施行前に弁護士・司法書士等の有資格者によるレビューが必要です。' +
  'レビューが完了するまで、この文書を根拠に契約・請求・アカウント停止を行ってはいけません。';

function draftBlocks(profile: LegalIssuerProfile): LegalBlock[] {
  return profile.draft ? [{ kind: 'notice', tone: 'warning', text: DRAFT_NOTICE }] : [];
}

/** 未設定の値は空文字ではなく「（未設定）」に見えると事故が減る。 */
function v(value: string): string {
  return value.trim().length > 0 ? value.trim() : '（未設定）';
}

/* ------------------------------------------------------------------ 利用規約 */

function termsDocument(p: LegalIssuerProfile): LegalBlock[] {
  const service = v(p.serviceName);
  return [
    ...draftBlocks(p),
    {
      kind: 'paragraph',
      text:
        `本利用規約（以下「本規約」）は、${v(p.companyName)}（以下「当社」）が提供する` +
        `メールホスティングサービス「${service}」（以下「本サービス」）の提供条件および` +
        `当社と契約者との間の権利義務関係を定めるものです。`,
    },
    { kind: 'section', number: '第 1 条', heading: '適用' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '本規約は、本サービスの利用に関する当社と契約者との間の一切の関係に適用されます。',
        `当社が本サービス上に掲示する「禁止事項（AUP）」（版 ${LEGAL_DOCUMENT_VERSIONS.aup}）、` +
          `「プライバシーポリシー」（版 ${LEGAL_DOCUMENT_VERSIONS.privacy}）、` +
          `「契約者データへのアクセス手続」（版 ${LEGAL_DOCUMENT_VERSIONS.data_access}）および` +
          '「委託先一覧」は、本規約の一部を構成します。',
        '当社は、電気通信事業法に基づき電気通信役務を提供する事業者として、同法第 4 条に定める通信の秘密を保護します。',
      ],
    },
    { kind: 'section', number: '第 2 条', heading: '定義' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '「契約者」とは、本規約に同意のうえ当社と本サービスの利用契約を締結した法人または個人をいいます。',
        '「利用者」とは、契約者が本サービス上に作成したメールボックスを実際に利用する者をいいます。',
        '「契約者データ」とは、契約者および利用者が本サービスに保存・送受信する電子メールその他の一切の情報をいいます。',
      ],
    },
    { kind: 'section', number: '第 3 条', heading: '契約の成立' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '利用契約は、申込者が本規約およびプライバシーポリシーに同意し、当社所定の方法で申込みを行い、当社がこれを承諾した時点で成立します。',
        '当社は、申込時に登録されたメールアドレスの到達確認が完了するまで、本サービスの提供を開始しません。',
        '申込内容に虚偽があった場合、過去に本規約または AUP に違反したことがある場合その他当社が不適当と判断した場合、当社は承諾を拒否できます。',
        // 民法 5 条の取消しに丸腰にならないための項。オンラインで誰でも
        // 申し込める導線である以上、未成年者の申込は現実に起こりうる。
        '申込者が未成年者である場合、法定代理人の同意を得たうえで申込みを行うものとします。当社は、必要と認めるときは同意の有無の確認を求めることができます。',
      ],
    },
    { kind: 'section', number: '第 4 条', heading: '料金および支払' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '契約者は、当社が別途定めるプランに応じた料金を、当社が指定する方法により支払うものとします。',
        // **実装（generateInvoice の期中按分）と一致させること。** 掲示だけ
        // 「日割り」と書いて実装が満額請求のままだと、それ自体が不当表示になる。
        '料金は月額とし、契約の開始月・終了月およびプラン変更のあった月は、当該プランで役務の提供を受けた日数に応じて日割りで按分計算します（1 円未満は切り捨て）。',
        '当社は、適格請求書発行事業者として、消費税法第 57 条の 4 に定める記載事項を満たす請求書を交付します。',
        '契約者が支払を遅滞した場合、当社は年 14.6% の割合による遅延損害金を請求できます。',
        /*
         * **年額は日割りしない**（`generateInvoice` が `prorated: false` で
         * 年額満額 1 行を出し、`buildPrepaidRefundLines()` は `!yearly` で
         * 明示的に除外している）。前項の「日割りで按分」だけを掲示して
         * 年額の扱いに触れないと、年払いの契約者が「解約月は日割り」と
         * 読んで申し込むことになる。
         */
        '年額プランを選択した場合、12 か月分を前払いいただきます。年額プランの料金は日割りの対象とせず、' +
          '期間の途中で解約した場合であっても、未経過期間に対応する料金の返金は行いません。',
        /*
         * 無料試用の日数は `TRIAL_PERIOD_DAYS`（`POST /public/signup` が
         * 実際に使っている値）から出す。**掲示だけ日数を書いて実装が別だと、
         * 「15 日目から課金」と書いた翌日に課金が始まる。**
         */
        /*
         * **「通知します」と書かない。** 試用終了前の予告通知
         * （`subscription.trial-reminder` 相当）は未実装で、実際に送っているのは
         * 移行**後**の 1 通だけ（`billing.ts` の「無料お試し期間が終了し、
         * 有料契約へ移行しました」）。実装していない手続を掲示すると、
         * それ自体が新しい掲示と実装の食い違いになる。
         * 事前の期間表示は申込確認画面（`/signup`）で行っている。
         */
        `無料試用期間は ${TRIAL_PERIOD_DAYS} 日間とし、その間は課金されません。` +
          '試用期間中に解約した場合、料金は発生しません。試用期間の満了日の翌日から有料契約へ移行し、課金を開始します。' +
          '課金の開始日は、お申込みの確認画面に表示します。',
      ],
    },
    { kind: 'section', number: '第 5 条', heading: '契約者の義務' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '契約者は、AUP を遵守し、利用者にも遵守させる責任を負います。',
        '契約者は、本サービスの認証情報を第三者に開示・貸与してはならず、その管理に一切の責任を負います。',
        '契約者は、自らが送信する電子メールについて、受信者の同意取得を含む適用法令（特定電子メール法等）の遵守に責任を負います。',
        '契約者は、当社が指示する DNS レコード（SPF・DKIM・DMARC）を設定するものとします。設定されない場合、送信の到達性は保証されません。',
      ],
    },
    { kind: 'section', number: '第 6 条', heading: '禁止行為および無催告即時停止' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '契約者は、AUP に定める行為を行ってはなりません。',
        '当社は、契約者または利用者が AUP に違反したと合理的に判断した場合、' +
          '**事前の催告なくただちに**本サービスの全部または一部の提供を停止し、または利用契約を解除できます。',
        /*
         * **全部免責にしない。** 消費者契約法 8 条 1 項は、事業者の債務不履行・
         * 不法行為による損害賠償責任を**全部**免除する条項を無効とする。
         * 申込は個人でも通る（`signupSchema` に法人性の確認は無い）ので、
         * ここは消費者契約になりうる。旧文面「一切の責任を負いません」は
         * 第 9 条 4 項の故意・重過失カーブアウトが掛からない位置にあり、
         * 争いになれば条項ごと使えない。免責の限度は第 9 条に一本化する。
         */
        '前項の停止・解除により契約者に生じた損害について、当社は第 9 条に定める限度で責任を負います。' +
          '停止・解除が契約者の本規約または AUP の違反に起因する場合、既に支払われた料金の返金は行いません。',
        '当社は、送信レピュテーションの保全のため、契約者ごとの送信レートに上限を設け、これを超える送信を拒否することがあります。',
      ],
    },
    { kind: 'section', number: '第 7 条', heading: '契約者データの取扱い' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '契約者データの内容に関する権利は契約者に帰属します。当社はこれを本サービスの提供に必要な範囲でのみ取り扱います。',
        /*
         * **「本文にアクセスしません」を実装に合わせて正確に書き直す。**
         *
         * 旧文面は「人が読むこと」と「機械が処理すること」を区別せずに
         * 両方を禁じていたが、実装は本文を機械的に処理している:
         *   - 迷惑メール・ウイルスの判定（rspamd / ClamAV）
         *   - Webメールの画像プロキシ（`webmail-message-proxy.ts` が
         *     RFC 822 の生ソースを受け取り、外部画像の URL を書き換える）
         *   - 契約者が有効にした場合の AI 機能（下の 4〜6 項）
         * 「人による閲覧」に限定して書けば説明できるものを、
         * 全部禁じる文面にしていたために掲示と実装が矛盾していた。
         */
        '当社の従業者は、通信の秘密を保護するため、契約者データの本文を閲覧しません。' +
          'ただし、法令に基づく要請、契約者からの明示的な依頼、または障害復旧のためやむを得ない場合に限り、' +
          '「契約者データへのアクセス手続」（本規約の一部を構成します）に定める手続' +
          '（申請理由の記録・別担当者の承認・時限付与・事後記録・契約者への通知）を経て閲覧することがあります。',
        '前項にかかわらず、当社は、本サービスの提供に必要な自動処理として、契約者データを人の閲覧を伴わずに機械的に処理します。' +
          '具体的には、迷惑メールおよびウイルスの判定、送信ドメイン認証の検証、' +
          '受信者の保護を目的とした電子メール中の外部画像の代理取得（画像プロキシ）、' +
          'ならびに配送に必要な経路制御です。これらの処理の結果として当社が記録するのは' +
          '判定結果・取得先ホスト・URL のハッシュ値等の外形情報であり、本文そのものを保存することはありません。',
        /*
         * ---- AI（生成 AI）機能。掲示に 1 文字も無いまま本文が外部 LLM へ
         * 出ていた。**既定は無効**（`orgAiPolicySchema.enabled` の default が
         * false）で、契約者が有効にしたときにだけ経路が開く。
         * `privacyLevel` は `metadata_only` / `excerpt` / `full_body` の 3 段で、
         * 契約者が機能単位に選べる。ここに書いた内容と
         * `packages/shared/src/ai-policy.ts` の実装を必ず一致させること。
         */
        '当社は、Webメールにおいて、要約・返信文の提案・自然文検索・自動分類等の生成 AI（大規模言語モデル）を用いた機能を提供します。' +
          '**この機能は既定では無効**で、契約者がコントロールパネルで有効にした場合に限り動作します。',
        '前項の機能を有効にした場合、当社は、当該機能の実行に必要な範囲で、電子メールの件名・差出人・宛先等のメタ情報' +
          'および契約者が選択した範囲の本文（抜粋または全体）を、当社が委託する大規模言語モデルの提供事業者へ送信します。' +
          '送信先の事業者名および取扱いが行われる国・地域は「委託先一覧」に掲載します。' +
          '取扱地域が日本国外である場合の取扱いは、プライバシーポリシーに定めるところによります。',
        '契約者は、本条 4 項の機能について、機能単位の有効・無効および本文を送信する範囲' +
          '（メタ情報のみ／本文の抜粋／本文の全体）をコントロールパネルからいつでも変更し、または停止できます。' +
          '当社は、当該機能の利用について、送信したトークン数および実行回数を記録しますが、送信した本文および生成された応答を保存しません。',
        '当社は、バックアップを暗号化して保管します。重複排除方式の性質上、特定の契約者データのみをバックアップから即時に削除することはできません。' +
          'バックアップ中のデータは最長 12 か月で自然に消滅します。',
        '契約者は、契約者データのバックアップを自ら取得する責任を負います。',
      ],
    },
    /*
     * **個人データの取扱いの委託（DPA）を標準約款に取り込む。**
     *
     * 契約者は `contacts` / `campaign_recipients` に第三者の個人データを預ける
     * 構造になっているのに、その委託の条件が契約文書に 1 項も無かった。
     * 委託元である契約者が個人情報保護法 25 条の「委託先の監督」を果たそうにも、
     * 監督の根拠になる合意が当社側に存在しない状態だった。
     * LP の「DPA の個別締結」を最上位プランの特典として掲示していたのも、
     * この条を置いていないことの裏返し。
     */
    { kind: 'section', number: '第 7 条の 2', heading: '個人データの取扱いの委託' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '契約者が本サービスに入力・保存する個人データ（配信先名簿の宛先を含みます）について、' +
          '契約者は個人情報取扱事業者として取扱いを決定する立場に、当社はその取扱いの委託を受ける立場に立ちます。',
        '当社は、委託された個人データを、本サービスの提供に必要な範囲を超えて取り扱いません。統計処理その他の目的で利用する場合は、個人を識別できない形式に加工します。',
        '当社は、委託された個人データについて、プライバシーポリシー「安全管理措置」に定める措置を講じ、取り扱う従業者を必要最小限に限り、これを監督します。',
        '当社は、業務の一部を再委託することがあります。再委託先は「委託先一覧」に掲載し、当社は再委託先に対して本条と同等の義務を負わせ、その履行について責任を負います。',
        '当社は、委託された個人データの漏えい・滅失・毀損その他の事故を認識した場合、遅滞なく契約者へ通知し、事実関係・影響範囲および再発防止策を報告します。',
        '当社は、本契約の終了後、第 8 条に定める保持期間の経過をもって、委託された個人データを消去します。' +
          '契約者は、消去の完了について当社の記録による報告を求めることができます。',
        '契約者が別途の書面による委託契約の締結を必要とする場合、当社は協議に応じます。本条は、当該書面が締結されるまでの間の合意として適用されます。',
      ],
    },
    { kind: 'section', number: '第 8 条', heading: '契約の終了とデータの削除' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '契約者は、当社所定の方法により、いつでも本契約を解約できます。解約は当該課金期間の末日に効力を生じます。',
        '当社は、解約または解除の効力発生日から 30 日間、契約者データを取得可能な状態で保持します。',
        '前項の期間経過後、当社は契約者データを削除します。削除の完了は当社の記録をもって確認します。',
        '請求・監査のために必要な記録（請求書・監査ログ）は、法令に定める期間保存します。',
      ],
    },
    { kind: 'section', number: '第 9 条', heading: '保証の否認および責任の制限' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '当社は、本サービスが契約者の特定の目的に適合すること、期待する機能・正確性・有用性を有すること、' +
          '電子メールが常に遅滞なく到達することを保証しません。',
        '当社の責に帰すべき事由により契約者に損害が生じた場合であっても、当社の賠償責任は、' +
          '損害発生時点から遡って 12 か月間に契約者が当社に支払った利用料金の総額を上限とします。',
        '当社は、逸失利益・事業機会の喪失その他の特別損害について責任を負いません。',
        '前 2 項は、当社に故意または重過失がある場合には適用しません。',
      ],
    },
    { kind: 'section', number: '第 10 条', heading: 'サービス内容の変更・中断・終了' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '当社は、本サービスの内容を変更し、または提供を終了することがあります。終了する場合は 90 日前までに契約者へ通知します。',
        '当社は、保守・障害・不可抗力等のため、事前の通知なく本サービスの提供を一時的に中断することがあります。',
      ],
    },
    { kind: 'section', number: '第 11 条', heading: '規約の変更' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '当社は、民法第 548 条の 4 の定めに従い、本規約を変更することがあります。',
        // 日数は `PRICE_CHANGE_NOTICE_DAYS`（実装が実際に強制している値）から出す。
        `契約者に不利益となる変更を行う場合、当社は変更内容および効力発生日を効力発生日の ${PRICE_CHANGE_NOTICE_DAYS} 日前までに通知し、` +
          '本サービス上での同意取得を行います。',
        /*
         * **通知の方法と効力発生日の掲示**（`LEGAL_DOCUMENT_SCHEDULE` の実装と対応）。
         * 「30 日前までに通知する」とだけ書いて通知の経路を書かないと、
         * 当社が何をすれば通知したことになるのかが決まらず、履行を証明できない。
         * 実装では各法定ページの応答に `version` / `currentVersion` /
         * `effectiveFrom` を載せ、効力発生日までは前版を現行として扱う。
         */
        '前項の通知は、本サービスの管理画面における掲示および契約者が届け出た連絡先への電子メールにより行います。' +
          '各法定ページには、変更後の版番号・効力発生日・効力発生日まで適用される版番号を表示します。',
        /*
         * **効力発生日までに同意しなかった契約者の扱い**。ここを定めないと、
         * 「未同意のまま使い続けている契約者」に対してどちらの版が適用されるのかが
         * 決まらない（民法 548 条の 4 の変更の効力と、当社が別途求める個別同意の
         * 関係が宙に浮く）。解約の申出を受け付けたうえで、継続利用をみなし同意とする。
         */
        '契約者は、変更後の本規約に同意しない場合、効力発生日の前日までに解約を申し出ることができます。' +
          '効力発生日までに解約の申出がなく、効力発生日以降も本サービスの利用を継続された場合、' +
          '契約者は変更後の本規約に同意したものとみなします。',
        '当社は、契約者が同意した規約の版番号と、同意した時点の文面のハッシュ値を記録します。',
      ],
    },
    /*
     * 反社会的勢力の排除。無いと暴力団排除条例への対応を求める法人顧客と
     * 決済代行の審査で必ず指摘される（`BILLING_PROVIDER=stripe` の審査を含む）。
     */
    { kind: 'section', number: '第 11 条の 2', heading: '反社会的勢力の排除' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '当社および契約者は、自らが暴力団、暴力団員、暴力団準構成員、暴力団関係企業、総会屋、社会運動等標榜ゴロ、' +
          '特殊知能暴力集団その他これらに準ずる者（以下「反社会的勢力」）に該当しないこと、' +
          'および反社会的勢力と社会的に非難されるべき関係を有しないことを表明し、保証します。',
        '当社および契約者は、自らまたは第三者を利用して、暴力的な要求行為、法的な責任を超えた不当な要求行為、' +
          '取引に関して脅迫的な言動をし、もしくは暴力を用いる行為、風説を流布し、偽計もしくは威力を用いて相手方の信用を毀損し、' +
          'または相手方の業務を妨害する行為を行わないことを確約します。',
        '当社または契約者は、相手方が前 2 項に違反した場合、何らの催告を要せずに本契約を解除することができます。' +
          'この場合、解除した当事者は、これにより相手方に生じた損害を賠償する責任を負いません。',
      ],
    },
    { kind: 'section', number: '第 12 条', heading: '準拠法および管轄' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '本規約の準拠法は日本法とします。',
        '本サービスに関して当社と契約者との間に生じた紛争については、当社の本店所在地を管轄する地方裁判所を第一審の専属的合意管轄裁判所とします。',
      ],
    },
    {
      kind: 'table',
      rows: [
        { label: '事業者', value: v(p.companyName) },
        { label: '所在地', value: v(p.address) },
        { label: 'お問い合わせ', value: v(p.contactEmail) },
        { label: '版', value: LEGAL_DOCUMENT_VERSIONS.terms },
      ],
    },
  ];
}

/* --------------------------------------------------------- プライバシーポリシー */

function privacyDocument(p: LegalIssuerProfile): LegalBlock[] {
  return [
    ...draftBlocks(p),
    {
      kind: 'paragraph',
      text:
        `${v(p.companyName)}（以下「当社」）は、個人情報の保護に関する法律（以下「個人情報保護法」）および` +
        `電気通信事業法に基づき、本サービス「${v(p.serviceName)}」の提供にあたって取得する個人情報を` +
        '以下のとおり取り扱います。',
    },
    { kind: 'section', number: '1', heading: '取得する情報' },
    {
      kind: 'table',
      rows: [
        { label: '契約情報', value: '商号・担当者名・住所・電話番号・請求先メールアドレス' },
        { label: '認証情報', value: 'ログイン用メールアドレス、パスワードのハッシュ値、二要素認証の設定' },
        { label: 'アクセス記録', value: '接続元 IP アドレス、User-Agent、操作日時、操作内容（監査ログ）' },
        {
          label: '認証記録',
          value:
            'Webメール・メールソフト（IMAP / POP3 / SMTP）の認証の成否、認証に用いたユーザー名、接続元 IP アドレス、日時',
        },
        {
          label: '通信の外形情報',
          value: '電子メールの送受信日時・送信元・宛先・サイズ・配送結果（本文は含みません）',
        },
        /*
         * 画像プロキシ（`image_proxy_objects`）。**受信者保護のための自動処理**で
         * 取得先ホスト・URL のハッシュ・出口 IP を記録する。取得情報表に
         * 挙げていないと、利用規約の「自動処理は行う」という記載と
         * プライバシーポリシーの取得情報が対応しない。
         */
        {
          label: '画像の代理取得の記録',
          value:
            '電子メール中の外部画像を当社が代理取得した際の取得先ホスト名・URL のハッシュ値・取得日時（本文および画像の内容は保存しません）',
        },
        /*
         * AI 機能。**契約者が有効にしたときにだけ発生する**ので、
         * 「取得する情報」の表にその条件ごと書く。
         */
        {
          label: 'AI 機能の利用記録',
          value:
            '契約者が生成 AI 機能を有効にした場合の利用日時・機能種別・送信したトークン数（プロンプト本文および生成された応答は保存しません）',
        },
        { label: '課金情報', value: '請求金額・入金記録。クレジットカード番号は当社では保持しません' },
      ],
    },
    {
      kind: 'notice',
      tone: 'info',
      /*
       * **「暗号化されたストレージ」と書かない。** 実装で暗号化しているのは
       * バックアップ（restic）と、認証情報・鍵の列（`*_enc`）だけで、
       * Maildir の実体・S3 上の配信本文・請求書 PDF は平文で置かれる。
       * ボリューム暗号化（LUKS）も S3 のサーバサイド暗号化も入っていない。
       * 実体の無い安全管理措置を掲示すると、侵害時の説明責任を果たせない。
       */
      text:
        '当社は、契約者が本サービスに保存・送受信する電子メールの**本文**を、コントロールプレーンのデータベースに保存しません。' +
        '本文はメールサーバー上のメールボックス領域にのみ存在し、当該領域へのアクセスは権限を分離した経路に限定しています。',
    },
    { kind: 'section', number: '2', heading: '利用目的' },
    {
      kind: 'list',
      ordered: false,
      items: [
        '本サービスの提供、本人確認および契約の履行',
        '料金の請求および適格請求書の交付',
        '障害の検知・復旧、不正利用（スパム送信・アカウント乗っ取り）の検知と防止',
        '本サービスに関する重要なお知らせ（障害・仕様変更・規約改定）の通知',
        '統計処理された利用状況の分析（個人を識別できない形式に限ります）',
      ],
    },
    { kind: 'section', number: '3', heading: '通信の秘密' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '当社は、電気通信事業法第 4 条に基づき、取扱中に係る通信の秘密を侵してはならない義務を負います。',
        // **「人による閲覧」と「自動処理」を分けて書く**（利用規約 第 7 条と対応）。
        // 分けずに全部を禁じる書き方にしていたため、迷惑メール判定・画像プロキシ・
        // AI 機能という実際に動いている処理が、掲示と正面から矛盾していた。
        '当社の従業者が電子メールの本文を閲覧するのは、①契約者からの明示的な依頼、②裁判所の令状その他法令に基づく正当な要請、' +
          '③人の生命・身体・財産の保護のため緊急やむを得ない場合に限られます。',
        '前項の閲覧は、申請理由の記録・当該申請者以外の管理者による承認・時限付きの権限付与・事後の記録という手続を経てのみ行われ、' +
          '実施の事実は原則として契約者へ通知されます（法令により通知が禁じられる場合を除きます）。手続の詳細は「契約者データへのアクセス手続」に掲示しています。',
        '前 2 項とは別に、当社は、本サービスの提供に必要な自動処理として、人の閲覧を伴わずに本文を機械的に処理します' +
          '（迷惑メール・ウイルスの判定、送信ドメイン認証の検証、受信者の保護を目的とした外部画像の代理取得、配送に必要な経路制御）。' +
          'これらは通信当事者の利益のために必要かつ相当な範囲で行い、処理の結果として記録するのは外形情報に限られます。',
        '契約者が生成 AI 機能を有効にした場合、当社は、当該機能の実行に必要な範囲で本文（契約者が選択した範囲の抜粋または全体）を' +
          '委託先の大規模言語モデル提供事業者へ送信します。**この機能は既定では無効**であり、' +
          '契約者は機能単位の有効・無効および送信する範囲をいつでも変更・停止できます。',
      ],
    },
    { kind: 'section', number: '4', heading: '第三者提供・委託' },
    {
      kind: 'paragraph',
      text:
        '当社は、個人情報保護法に定める場合を除き、あらかじめ本人の同意を得ることなく個人情報を第三者に提供しません。' +
        '業務委託に伴う提供については「委託先一覧」に掲載しています。',
    },
    /*
     * **越境移転（法 28 条）の情報提供**。共有の AI プロバイダは
     * anthropic / openai（いずれも米国）を既定の選択肢に含んでいるので、
     * 「委託先一覧に載せる」だけでは足りず、外国にある第三者への提供に
     * あたる場合の説明義務がある。ここで根拠と参照先を明示する。
     */
    { kind: 'section', number: '4-2', heading: '外国にある第三者への提供（越境移転）' },
    {
      kind: 'paragraph',
      text:
        '当社が業務を委託する事業者には、取扱いが日本国外で行われるものが含まれます。' +
        '契約者が生成 AI 機能を有効にした場合の大規模言語モデル提供事業者は、その代表例です。' +
        '当社は、個人情報保護法第 28 条に基づき、当該国の名称、当該国における個人情報の保護に関する制度、' +
        'および当該事業者が講ずる個人情報の保護のための措置に関する情報を、「委託先一覧」への掲載またはご請求に応じた提供により明らかにします。' +
        '生成 AI 機能を有効にしない場合、本項の移転は発生しません。',
    },
    { kind: 'section', number: '5', heading: '保存期間' },
    {
      kind: 'table',
      rows: [
        { label: '契約情報', value: '契約終了後 5 年（会社法・法人税法の保存義務期間）' },
        { label: '請求関連', value: '交付・受領から 7 年（消費税法・電子帳簿保存法）' },
        { label: '監査ログ', value: '7 年（改ざん検知のため Object Lock 付きストレージへ複製）' },
        /*
         * **実装（`maillog.retention`）と同じ日数を定数から出す。**
         * 掲示 90 日 / 実装 180 日でずれていた。掲示のほうが短かったので、
         * 「90 日で消えます」と読んだ契約者の期待より 3 か月長く
         * 宛先アドレスが残っていたことになる。
         * 解約後の消去（`contract.purge-org`）の対象外である点も明記する
         * （「契約終了の 30 日後に消去」だけを読むと期待と食い違う）。
         */
        {
          label: '配送ログ',
          value:
            `${MAIL_LOG_RETENTION_DAYS} 日（送受信の外形情報。契約終了後もこの期間が経過するまで保持します）`,
        },
        {
          label: '認証記録',
          value:
            `${Math.round(AUTH_EVENT_RETENTION_DAYS / 365)} 年（不正アクセスの検知と調査のため。契約終了後もこの期間が経過するまで保持します）`,
        },
        /*
         * **解約後の消去は実装（`contract.purge-org`）と同じ日数を書く。**
         * ここに書いた期日で実際に消えないなら、それは掲示のほうが嘘になる。
         * 消す対象と残す対象も名指しする（「全部消えます」と読まれると、
         * 同意文言が残っていることが後から問題になる）。
         */
        {
          label: '配信先名簿・配信停止リスト',
          value: '契約終了の 30 日後に消去（宛先・展開済み宛先・申込フォームの送信内容・配信本文を含みます）',
        },
        {
          label: '申込フォームの同意文言',
          value: '契約終了後も保持（どの文言で同意を得たかの記録。個人を特定する情報は含みません）',
        },
        { label: 'バックアップ', value: '最長 12 か月（重複排除方式のため個別削除はできません）' },
      ],
    },
    { kind: 'section', number: '6', heading: '開示・訂正・削除等の請求' },
    {
      kind: 'paragraph',
      text:
        `本人からの開示、訂正、利用停止、消去、利用目的の通知、第三者提供記録の開示および` +
        `データの可搬（ご自身のデータを一般的な形式で受け取ること）のご請求は、${v(p.privacyContactEmail)} 宛にご連絡ください。` +
        '当社は、ご本人であることを確認のうえ、法令に従い遅滞なく対応します。',
    },
    /*
     * **受付から回答までの手続を具体的に書く。**
     *
     * 実装（`data_subject_requests`）は受付・本人確認・期限・完了・拒否理由を
     * 台帳で管理していて、期限は受付から `DATA_SUBJECT_REQUEST_DUE_DAYS` 日で
     * 確定する。ところが掲示は「遅滞なく対応します」の 1 文だけで、
     * **請求者はいつまでに回答が来るのかを知る手段が無かった**（実装の側は
     * 期限超過を検出して一覧に出しているのに、その期限を約束していない）。
     * 手数料・代理人・拒否理由の説明・苦情の申出先も同時に掲示する
     * （個人情報保護法 33〜38 条の求めに対応する運用上の最低限）。
     */
    {
      kind: 'list',
      ordered: true,
      items: [
        'ご請求は受付台帳に記録し、受付番号をお知らせします。',
        `当社は、ご本人であることを確認できた後、受付から ${DATA_SUBJECT_REQUEST_DUE_DAYS} 日以内の回答を目安として対応します。` +
          '調査に時間を要する場合は、その旨と見込みの時期をあらかじめご連絡します。',
        '本人確認が取れるまで、当社は保有個人データの内容を開示しません。' +
          '代理人によるご請求の場合は、委任状その他の代理権を証する書面を併せてご提出ください。',
        '開示等のご請求に手数料はいただきません。',
        '法令によりご請求に応じられない場合、または一部に限ってお応えする場合は、その理由を説明します。',
        'メールボックスの内容はメールアドレス単位の書き出し機能で、配信先名簿は CSV の書き出し機能でご自身でも取得いただけます。',
        '当社の対応にご納得いただけない場合、個人情報保護委員会へ申し出ることができます。',
      ],
    },
    /*
     * 法執行機関からの照会窓口（R46）。**掲示していないと、令状が
     * サポート窓口や abuse 窓口に届いて回答期限を落とす。**
     * 専用アドレスを env に足すのが本来だが、そこは所有外のため
     * 事業者の連絡先（`contactEmail`）を正面の窓口として明示する。
     */
    { kind: 'section', number: '6-2', heading: '法令に基づく開示の請求（法執行機関等）' },
    {
      kind: 'paragraph',
      text:
        `捜査関係事項照会、令状その他法令に基づく開示のご請求は、${v(p.contactEmail)} 宛に書面でご連絡ください。` +
        '当社は、電気通信事業法第 4 条（通信の秘密）に照らして請求の根拠と範囲を確認したうえで、' +
        '応じる義務がある範囲に限って対応します。対応の事実は、法令により禁じられる場合を除き契約者へ通知します。',
    },
    { kind: 'section', number: '7', heading: '安全管理措置' },
    {
      kind: 'list',
      ordered: false,
      items: [
        '組織的措置: 権限を職務に応じて分離し、顧客データへのアクセスをすべて監査ログに記録します。',
        '人的措置: 従業者と秘密保持契約を締結し、通信の秘密に関する社内規程に基づく教育を行います。',
        '物理的措置: サーバーはデータセンター事業者の管理する施設に設置しています。',
        /*
         * **「保存データの暗号化」と一般化して書かない。**
         * 実際に暗号化しているのは (a) バックアップ全体（restic）と
         * (b) 認証情報・鍵の列（`*_enc`。DKIM 秘密鍵 / TLS 秘密鍵 /
         * ノードのサービス資格情報 / 外部 API キー / TOTP シークレット）だけ。
         * ボリューム暗号化（LUKS）も S3 のサーバサイド暗号化も入っていない。
         * 実体の無い措置を掲示すると、景表法・個人情報保護法上の説明義務と
         * 侵害時の説明責任の両方で持たない。**LUKS と SSE が入ったら
         * ここを「保存データの暗号化」に戻す。**
         */
        '技術的措置: 通信の暗号化（TLS）、バックアップの暗号化、認証情報および暗号鍵の暗号化保管、' +
          'パスワードの不可逆ハッシュ化（Argon2id）、二要素認証を実施しています。',
      ],
    },
    { kind: 'section', number: '8', heading: 'Cookie 等' },
    {
      kind: 'paragraph',
      text:
        '当社は、ログインセッションの維持のために必要な Cookie のみを使用します。' +
        '広告・行動ターゲティングを目的とした第三者 Cookie は使用していません。',
    },
    { kind: 'section', number: '9', heading: '改定' },
    {
      kind: 'paragraph',
      text:
        '本ポリシーを重要な点で変更する場合、当社は本サービス上で告知し、必要に応じて改めて同意を取得します。' +
        '契約者が同意した版番号と文面のハッシュ値は当社が記録します。',
    },
    {
      kind: 'table',
      rows: [
        { label: '事業者', value: v(p.companyName) },
        // 個人情報保護法 32 条 1 項 1 号・施行令 10 条の公表事項
        // （名称・住所・**代表者の氏名**）。特商法ページと同じ書式で出す。
        { label: '代表者', value: v(p.representativeName) },
        { label: '所在地', value: v(p.address) },
        {
          label: '個人情報保護管理者',
          value: p.privacyOfficer.trim().length > 0 ? p.privacyOfficer : '個人情報保護管理者',
        },
        { label: '問い合わせ窓口', value: v(p.privacyContactEmail) },
        { label: '版', value: LEGAL_DOCUMENT_VERSIONS.privacy },
      ],
    },
  ];
}

/* ------------------------------------------------------------------- AUP */

function aupDocument(p: LegalIssuerProfile): LegalBlock[] {
  return [
    ...draftBlocks(p),
    {
      kind: 'paragraph',
      text:
        `本 AUP は、${v(p.companyName)}が提供する「${v(p.serviceName)}」の利用規約の一部を構成します。` +
        '本サービスは複数の契約者が送信 IP アドレスを共有するため、1 契約者の違反が他のすべての契約者の' +
        'メール到達性を損ないます。以下の禁止事項は、その構造的な理由から厳格に運用されます。',
    },
    {
      kind: 'notice',
      tone: 'warning',
      text:
        '本 AUP に違反したと当社が合理的に判断した場合、当社は**事前の催告なくただちに**' +
        '送信の停止、メールボックスの凍結、または利用契約の解除を行います。返金は行いません。',
    },
    { kind: 'section', number: '1', heading: '迷惑メールの送信' },
    {
      kind: 'list',
      ordered: false,
      items: [
        '受信者の事前の同意（オプトイン）を得ていない電子メールの送信',
        '購入・レンタル・収集（スクレイピング）により取得したアドレスリストへの送信',
        '配信停止の意思表示を受けた宛先への再送信',
        '特定電子メール法に定める表示義務（送信者情報・配信停止の方法）を欠く送信',
        '送信者情報を偽り、または実在しない送信元を詐称する行為',
        '同一または類似の内容を、宛先を分散させて大量に送信する行為',
      ],
    },
    { kind: 'section', number: '2', heading: '第三者中継・オープンリレー' },
    {
      kind: 'list',
      ordered: false,
      items: [
        '本サービスを第三者に再販・転貸し、当社の承諾なく第三者のメール送信を中継する行為',
        '認証を要しない中継経路（オープンリレー・オープンプロキシ）を構成する行為',
        '本サービスの認証情報を第三者と共有し、実質的に不特定多数に送信させる行為',
        '転送設定を用いて、当社の許可なく大量のメールを外部へ中継する行為',
      ],
    },
    { kind: 'section', number: '3', heading: 'フィッシング・なりすまし' },
    {
      kind: 'list',
      ordered: false,
      items: [
        '実在する事業者・行政機関・個人を装い、認証情報や決済情報を詐取しようとする行為',
        '受信者を欺くことを目的として、他者の商標・ロゴ・意匠を無断で使用する行為',
        '他者のドメインを送信者として詐称する行為（当社は SPF・DKIM・送信者一致検査によりこれを技術的に拒否します）',
        'マルウェア、ランサムウェア、その他の有害なプログラムを含む、または誘導する電子メールの送信',
      ],
    },
    { kind: 'section', number: '4', heading: 'システムへの攻撃・妨害' },
    {
      kind: 'list',
      ordered: false,
      items: [
        '本サービスまたは第三者のシステムに対する不正アクセス、脆弱性の探索、負荷試験（当社の書面による事前承諾がある場合を除く）',
        '本サービスのレート制限・容量制限・認証機構を回避しようとする行為',
        '当社が付与した以外の IP アドレス・ホスト名を用いて本サービスの送信元を偽装する行為',
      ],
    },
    { kind: 'section', number: '5', heading: '違法・有害なコンテンツ' },
    {
      kind: 'list',
      ordered: false,
      items: [
        '法令に違反する物品・役務の販売、勧誘または広告',
        '児童ポルノその他の違法なコンテンツの保存・送信',
        '知的財産権、名誉権、プライバシー権その他の権利を侵害するコンテンツの送信',
      ],
    },
    { kind: 'section', number: '6', heading: '送信レートおよび試用期間中の制限' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '当社は、共有 IP アドレスの評判を保全するため、契約者ごとに 1 時間あたり・1 日あたりの送信通数の上限を設けます。',
        '無料試用期間中は、有料プランよりも大幅に低い上限が適用されます。',
        '新規に割り当てた送信 IP アドレスについては、段階的に上限を引き上げるウォームアップ期間を設けます。',
        '上限を超える送信は一時的に拒否されます。恒常的に上限を超える場合はプランの変更をご検討ください。',
      ],
    },
    { kind: 'section', number: '7', heading: '通報と対応' },
    {
      kind: 'paragraph',
      text:
        `本サービスから送信された電子メールに関する苦情・通報は ${v(p.abuseEmail)} で受け付けます。` +
        '当社は、通報の内容を確認のうえ、必要と判断した場合はただちに送信を停止します。' +
        '当社は、バウンス率・苦情率・ブロックリストへの掲載状況を継続的に監視しており、' +
        '閾値を超えた契約者に対しては通知の有無にかかわらず送信を制限することがあります。',
    },
    {
      kind: 'table',
      rows: [
        { label: '事業者', value: v(p.companyName) },
        { label: '通報先', value: v(p.abuseEmail) },
        { label: '版', value: LEGAL_DOCUMENT_VERSIONS.aup },
      ],
    },
  ];
}

/* -------------------------------------------------------------------- SLA */

/** basis point の稼働率を掲示用の文字列にする（9950 → `99.5%`）。 */
function formatUptimeBp(bp: number): string {
  const percent = bp / 100;
  return `${Number.isInteger(percent) ? percent.toFixed(1) : String(percent)}%`;
}

/**
 * サービスクレジットの階梯表を**コードの階梯定義から**組み立てる。
 *
 * **文面と定数の二重管理をやめるための関数。** 以前は掲示側に手書きの表が
 * あり、コード側（`DEFAULT_SLA_TIERS`）と食い違っていた。掲示に無い 20% の段が
 * コードにあり、掲示した 100% の段がコードに無い＝**掲示した債務を
 * 履行できない**状態だった。ここから作れば、階梯を変えたら掲示も必ず動く。
 */
function slaCreditRows(): { label: string; value: string }[] {
  const tiers = [...DEFAULT_SLA_TIERS].sort((a, b) => b.belowBp - a.belowBp);
  return tiers.map((tier, i) => {
    const lower = tiers[i + 1];
    const label = lower
      ? `${formatUptimeBp(tier.belowBp)} 未満 ${formatUptimeBp(lower.belowBp)} 以上`
      : `${formatUptimeBp(tier.belowBp)} 未満`;
    return { label, value: `当月利用料金の ${tier.creditBp / 100}%` };
  });
}

function slaDocument(p: LegalIssuerProfile): LegalBlock[] {
  const blocks: LegalBlock[] = [
    ...draftBlocks(p),
    {
      kind: 'paragraph',
      text:
        `${v(p.companyName)}は、「${v(p.serviceName)}」について以下のサービス品質目標を定めます。`,
    },
  ];

  if (!p.slaCreditsEnabled) {
    // 稼働率の実測と自動起票が動くまでは「目標値」として掲示する。
    // 測っていないのにクレジット条項を書くと、顧客の申告を検証も否認もできない
    // 債務だけが残る（R0 §2.1 の判断）。
    blocks.push({
      kind: 'notice',
      tone: 'info',
      text:
        '本ページに記載する数値は当社の**努力目標**であり、未達の場合の返金・クレジットを約束するものではありません。' +
        '稼働率の実測に基づくサービスクレジット制度は、上位プランの提供開始とあわせて導入します。',
    });
  }

  blocks.push(
    { kind: 'section', number: '1', heading: '対象' },
    {
      kind: 'paragraph',
      text:
        '本目標は、メールの受信（SMTP）、送信（Submission）、取得（IMAP / POP3）およびコントロールパネルを対象とします。' +
        'DNS の設定不備、契約者のネットワーク、受信側事業者の判断による遅延・拒否は対象外です。',
    },
    { kind: 'section', number: '2', heading: '目標値' },
    {
      kind: 'table',
      rows: [
        { label: 'メール送受信（月間稼働率）', value: formatUptimeBp(SLA_UPTIME_GUARANTEE_BP) },
        { label: 'コントロールパネル（月間稼働率）', value: '99.0%' },
        {
          label: '計画メンテナンスの事前告知',
          value: `原則 ${PLANNED_MAINTENANCE_NOTICE_DAYS} 日前まで`,
        },
        {
          label: '計画メンテナンスの上限',
          value: `1 か月あたり合計 ${PLANNED_MAINTENANCE_MONTHLY_MINUTES_MAX / 60} 時間まで`,
        },
        { label: '重大障害の一次報告', value: '検知から 60 分以内' },
        { label: '障害の事後報告書', value: '復旧から 5 営業日以内' },
      ],
    },
    { kind: 'section', number: '3', heading: '稼働率の算定' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '稼働率は「(当月の総分数 − 停止分数) ÷ 当月の総分数」で算出します。',
        '停止分数には、事前に告知した計画メンテナンス、不可抗力、契約者側の設定に起因する事象、' +
          'および AUP 違反に対する当社の停止措置は含めません。',
        `計画メンテナンスとして稼働率の算定から除外できる時間は、1 か月あたり合計 ` +
          `${PLANNED_MAINTENANCE_MONTHLY_MINUTES_MAX / 60} 時間を上限とします。` +
          `これを超える停止は、事前に告知した場合であっても停止分数に算入します。`,
        '当社は、監視の記録をもって停止の有無を判断します。契約者からの申告があった場合は、' +
          '当社の記録と併せて判断します。',
      ],
    },
    { kind: 'section', number: '4', heading: 'サポート' },
    {
      kind: 'table',
      rows: [
        { label: '受付方法', value: 'コントロールパネルおよび電子メール' },
        /*
         * **実装（`apps/api/src/services/support.ts` の `SUPPORT_HOURS` と
         * `supportSlaFor()`）と同じ値を掲示する。** 掲示 10:00〜18:00 /
         * 実装 9:00〜18:00 でずれており、9:15 に届いたチケットは
         * 「掲示上はまだ受付前なのに実装上は営業時間内」だった。
         * SLA 超過の判定もこの時計で動くので、ずれたままだと
         * 掲示に対する遵守状況を説明できない。
         */
        {
          label: '受付時間',
          value:
            `平日 ${SUPPORT_BUSINESS_HOURS.startHour}:00〜${SUPPORT_BUSINESS_HOURS.endHour}:00` +
            '（日本時間、当社休業日を除く）。受付時間外に届いたお問い合わせは、翌営業日の受付開始時刻に受け付けたものとして期限を計算します。',
        },
        {
          label: '一次回答の目安',
          value:
            `優先サポート対象プラン: 受付時間の ${SUPPORT_FIRST_RESPONSE_MINUTES.priority / 60} 時間以内 ／ ` +
            `その他のプラン: 受付時間の ${SUPPORT_FIRST_RESPONSE_MINUTES.standard / 60} 時間（1 営業日）以内。` +
            'いずれも受付時間のみを数え、受付時間外の経過は算入しません。',
        },
        { label: '緊急連絡先', value: v(p.contactEmail) },
      ],
    },
  );

  if (p.slaCreditsEnabled) {
    const covered = PLAN_CATALOG.filter((plan) => plan.limits.slaUptimeBp > 0).map((p2) => p2.name);
    blocks.push(
      { kind: 'section', number: '5', heading: 'サービスクレジット' },
      {
        kind: 'paragraph',
        text:
          covered.length > 0
            ? `サービスクレジットの対象は ${covered.join('・')} の各プランです。` +
              'それ以外のプランについて、本ページの数値は努力目標であり返金の対象となりません。'
            : '現在、サービスクレジットの対象となるプランはありません。',
      },
      { kind: 'table', rows: slaCreditRows() },
      {
        kind: 'list',
        ordered: true,
        items: [
          'クレジットは翌月以降の請求から控除する形で提供し、現金による返金は行いません。',
          'クレジットの請求は、対象月の翌月末日までに当社所定の方法（コントロールパネルの申告フォーム）で行うものとします。',
          '当社が監視の記録から未達を検知した場合は、契約者からの申告がなくても当社が起票します。',
          'クレジットは、当社の債務不履行に対する契約者の唯一かつ排他的な救済手段とします。',
        ],
      },
    );
  }

  blocks.push({
    kind: 'table',
    rows: [
      { label: '事業者', value: v(p.companyName) },
      { label: '版', value: LEGAL_DOCUMENT_VERSIONS.sla },
    ],
  });
  return blocks;
}

/* ------------------------------------------------- 特定商取引法に基づく表記 */

function tokushohoDocument(p: LegalIssuerProfile): LegalBlock[] {
  const rows: { label: string; value: string }[] = [
    { label: '販売事業者', value: v(p.companyName) },
    { label: '代表者', value: v(p.representativeName) },
    { label: '所在地', value: `${v(p.postalCode)} ${v(p.address)}` },
    { label: '電話番号', value: v(p.phone) },
    {
      label: '電話受付時間',
      // SLA ページの受付時間と 1 つの値から出す（片方だけ古くなるのを防ぐ）。
      value:
        `平日 ${SUPPORT_BUSINESS_HOURS.startHour}:00〜${SUPPORT_BUSINESS_HOURS.endHour}:00` +
        '（当社休業日を除く）。お問い合わせは電子メールでも承ります。',
    },
    { label: 'メールアドレス', value: v(p.contactEmail) },
    { label: 'ホームページ', value: `https://${v(p.serviceBaseDomain)}` },
    {
      label: '販売価格',
      value:
        '各プランの料金ページに表示する金額（消費税込みの総額を表示します）。' +
        '請求書は税抜の対価と消費税額を税率ごとに区分して記載します。' +
        '月額基本料は前払い、上限を超えた分の従量料金は後払いで、いずれも同じ請求書に記載します。' +
        '月額プランにおいて、プラン変更・契約開始・解約のあった月の料金は、日数に応じて日割りで按分計算します。' +
        // **年額は日割りしない**（実装が `prorated: false` で年額満額 1 行を出す）。
        // 「日割りで按分」だけを掲示すると、年払いの申込者が事実と異なる理解で申し込む。
        '年額プランは 12 か月分を前払いいただき、日割りの対象としません。' +
        /*
         * 無料試用の日数と課金開始日。**申込画面の表示（`/signup` の
         * 最終確認）と同じ値**（`TRIAL_PERIOD_DAYS`）から出す。
         */
        `新規のお申込みには ${TRIAL_PERIOD_DAYS} 日間の無料試用期間があり、その間の料金は発生しません。` +
        `試用期間の満了日の翌日から有料契約へ移行し、課金を開始します。`,
    },
    {
      /*
       * **超過（従量）の単価をここに実額で書く**（A267）。
       *
       * 上限超過で従量課金が発生するのに単価がどこにも掲示されていない状態は、
       * 特商法 11 条の「販売価格」の表示として不完全。単価は
       * `PLAN_CATALOG` から生成するので、カタログを変えれば掲示も動く。
       *
       * **送信通数は書かない。** 送信の超過は金額に変換せず、ノード側が
       * その日の送信を 450 で絞るだけ（REQUIREMENTS.md §8.2）。
       * 課金しない料金を掲示すると、掲示のほうが債務になる。
       */
      label: '超過分の従量料金',
      value:
        overagePriceRows()
          .map((row) => overagePriceLine(row))
          .join(' / ') +
        '。メールボックス数は当月の最大値、容量は当月のピーク値で判定し、' +
        '1 GiB 未満の端数は切り上げます。送信通数は超過しても課金せず、' +
        'その日の送信を一時的に制限します。',
    },
    {
      label: '商品代金以外の必要料金',
      value:
        'インターネット接続に必要な通信料金は契約者の負担となります。銀行振込の場合の振込手数料は契約者の負担です。',
    },
    {
      label: '支払方法',
      value: 'クレジットカード決済または銀行振込',
    },
    {
      label: '支払時期',
      value:
        'クレジットカード: 毎月の課金日に決済します。銀行振込: 請求書の発行日から 30 日以内にお支払いください。' +
        '年額プランは、契約開始日および以後 1 年ごとの応当日に 12 か月分をお支払いいただきます。' +
        `無料試用期間中の請求は発生せず、最初の請求は試用期間の満了日の翌日以降に行います（試用期間は ${TRIAL_PERIOD_DAYS} 日間）。`,
    },
    {
      label: '役務の提供時期',
      value:
        'お申込み後、登録メールアドレスの到達確認が完了した時点から順次提供を開始します（通常は即時）。',
    },
    {
      /*
       * **旧文面は 1 つの欄の中で自己矛盾していた。**
       * 「当該課金期間の末日をもって効力を生じます」（＝日割りの余地が無い）と
       * 「解約月の料金は日数に応じて日割りで按分計算します」と
       * 「既にお支払い済みの前払金の返金は行いません」が同居していた。
       * 実装（`buildPrepaidRefundLines()`）は月額について前払い分を返す側なので、
       * 最後の 1 文だけが実装と逆。返金する / しないの境界は
       * **月額か年額か**なので、そこで分けて書く。
       */
      label: '返品・キャンセル（解約）',
      value:
        '役務の性質上、提供開始後の返品はお受けできません。解約はコントロールパネルからいつでも行えます。' +
        '月額プランの場合、解約月の料金は役務の提供を受けた日数に応じて日割りで按分計算し、' +
        '前払い済みで役務の提供が行われなくなった期間に対応する金額は、最終の請求書で精算（相殺または返金）します。' +
        '年額プランの場合、料金は日割りの対象とせず、期間の途中で解約した場合であっても未経過期間に対応する料金の返金は行いません。' +
        `無料試用期間（${TRIAL_PERIOD_DAYS} 日間）中に解約した場合、料金は発生しません。`,
    },
    {
      label: '動作環境',
      value:
        'コントロールパネルの利用には、最新版の Google Chrome、Mozilla Firefox、Microsoft Edge または Safari が必要です。',
    },
  ];

  if (p.invoiceRegistrationNo.trim().length > 0) {
    rows.push({ label: '適格請求書発行事業者登録番号', value: p.invoiceRegistrationNo.trim() });
  }
  if (p.telecomNotificationNo.trim().length > 0) {
    rows.push({ label: '電気通信事業届出番号', value: p.telecomNotificationNo.trim() });
  }

  const blocks: LegalBlock[] = [...draftBlocks(p)];

  // 登録番号・届出番号は「未取得＝空」が正しい状態なので、必須項目にはしない。
  // ただし空であることを黙って隠すと「取得済みだが掲示し忘れ」と区別できないため、
  // 画面に理由を出す。
  if (p.invoiceRegistrationNo.trim().length === 0) {
    blocks.push({
      kind: 'notice',
      tone: 'info',
      text:
        '適格請求書発行事業者の登録番号は未登録です。登録が完了するまで、当社が交付する請求書は' +
        '適格請求書（インボイス）に該当しません。',
    });
  }
  if (p.telecomNotificationNo.trim().length === 0) {
    blocks.push({
      kind: 'notice',
      tone: 'info',
      text: '電気通信事業の届出番号は受理待ちです。受理後にこのページへ掲示します。',
    });
  }

  blocks.push(
    { kind: 'paragraph', text: '特定商取引に関する法律第 11 条に基づき、以下のとおり表示します。' },
    { kind: 'table', rows },
    {
      kind: 'table',
      rows: [{ label: '版', value: LEGAL_DOCUMENT_VERSIONS.tokushoho }],
    },
  );
  return blocks;
}

/* ---------------------------------------------------------------- 委託先一覧 */

function subcontractorsDocument(p: LegalIssuerProfile): LegalBlock[] {
  return [
    ...draftBlocks(p),
    {
      kind: 'paragraph',
      text:
        `${v(p.companyName)}は、「${v(p.serviceName)}」の提供にあたり、以下の事業者に業務の一部を委託しています。` +
        '当社は、個人情報保護法第 25 条に基づき、委託先に対して必要かつ適切な監督を行います。',
    },
    {
      kind: 'table',
      rows: p.subcontractors.map((s) => ({
        label: s.name,
        value: `${s.role}（取扱地域: ${s.region}）`,
      })),
    },
    /*
     * **生成 AI の提供事業者をここに載せることを明文で要求する。**
     * 委託先一覧は env の自由記述（`LEGAL_SUBCONTRACTORS`）なので、
     * 実際に使っている委託先が載っているかを機械的に検査できていない。
     * せめて「AI 機能を有効にした場合の送信先は本ページに載せる」ことを
     * 掲示側で約束しておかないと、プライバシーポリシー §4-2 の
     * 「委託先一覧に掲載します」という参照が宙に浮く。
     */
    { kind: 'section', number: '1', heading: '生成 AI 機能を有効にした場合の送信先' },
    {
      kind: 'paragraph',
      text:
        '契約者が Webメールの生成 AI 機能を有効にした場合、電子メールのメタ情報および契約者が選択した範囲の本文は、' +
        '上表のうち大規模言語モデルの提供を業務内容とする事業者へ送信されます。' +
        '当該事業者と取扱地域は上表に掲載します。生成 AI 機能を有効にしない場合、これらの事業者への送信は発生しません。',
    },
    { kind: 'section', number: '2', heading: '越境移転について' },
    {
      kind: 'paragraph',
      text:
        '取扱地域が日本国外である委託先については、個人情報保護法第 28 条に基づき、' +
        '当該国の個人情報保護制度および委託先が講ずる個人情報の保護のための措置に関する情報を、' +
        'ご請求に応じて提供します。',
    },
    { kind: 'section', number: '3', heading: '変更' },
    {
      kind: 'paragraph',
      text:
        '委託先を追加・変更する場合、当社は本ページを更新します。契約者データの取扱いに重要な影響を及ぼす変更については、' +
        '事前に本サービス上で告知します。',
    },
    {
      kind: 'table',
      rows: [
        { label: '問い合わせ窓口', value: v(p.privacyContactEmail) },
        { label: '版', value: LEGAL_DOCUMENT_VERSIONS.subcontractors },
      ],
    },
  ];
}

/* ------------------------------------- 契約者データへのアクセス手続（別紙） */

/**
 * 利用規約 第 7 条が参照する「別紙」の本体。
 *
 * **文面は実装の写しにする。** 統制そのものは
 * `apps/api/src/routes/ops/break-glass.ts` に実装済みで、申請理由の記録・
 * 別担当者の承認・時限付与・事後記録・契約者への通知がすべて動いている。
 * それを読めない場所に置いたままだと、掲示している統制の内容を契約者が
 * 確認できず、監査や大口顧客のセキュリティ質問票に答えられない。
 * **実装の手続を変えたら、この文書も同じコミットで変えること。**
 */
function dataAccessDocument(p: LegalIssuerProfile): LegalBlock[] {
  return [
    ...draftBlocks(p),
    {
      kind: 'paragraph',
      text:
        `本書は、利用規約 第 7 条 2 項に定める「別紙」であり、${v(p.companyName)}（以下「当社」）の従業者が` +
        '契約者データの本文を閲覧する場合に必ず経る手続を定めるものです。本書は利用規約の一部を構成します。',
    },
    { kind: 'section', number: '1', heading: '原則' },
    {
      kind: 'paragraph',
      text:
        '当社の従業者は、契約者データの本文を閲覧しません。本サービスの提供に必要な自動処理' +
        '（迷惑メール・ウイルスの判定、送信ドメイン認証の検証、受信者の保護を目的とした外部画像の代理取得、配送に必要な経路制御、' +
        'および契約者が有効にした場合の生成 AI 機能）は、人の閲覧を伴わずに機械的に行われます。',
    },
    { kind: 'section', number: '2', heading: '閲覧が認められる場合' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '契約者からの明示的な依頼がある場合（障害調査の依頼を含みます）。',
        '裁判所の令状、捜査関係事項照会その他法令に基づく正当な要請がある場合。',
        '人の生命・身体・財産の保護のため緊急やむを得ない場合。',
      ],
    },
    { kind: 'section', number: '3', heading: '手続' },
    {
      kind: 'list',
      ordered: true,
      items: [
        '申請: 閲覧を要する従業者は、対象の範囲（組織・メールボックス）、理由、根拠となる前条の号、および必要な期間を記録して申請します。',
        '承認: 申請者以外の管理者が承認します。申請者自身による承認はシステム上できません。',
        '時限付与: 承認された権限には有効期限が設定され、期限の到来により自動的に失効します。期間の延長は再度の申請と承認を要します。',
        '記録: 申請・承認・失効および実際に行われた操作は、改ざん検知を備えた監査ログに記録されます。記録は削除できません。',
        '通知: 当社は、閲覧を行った事実および理由を契約者へ通知します。法令により通知が禁じられる場合に限り通知を留保し、その理由を記録します。',
      ],
    },
    { kind: 'section', number: '4', heading: '契約者による確認' },
    {
      kind: 'paragraph',
      text:
        `契約者は、自らの組織に対して行われた本手続の実施状況について、${v(p.privacyContactEmail)} 宛に照会できます。` +
        '当社は、ご本人であることを確認のうえ、監査ログに基づいて回答します。',
    },
    {
      kind: 'table',
      rows: [
        { label: '事業者', value: v(p.companyName) },
        { label: '問い合わせ窓口', value: v(p.privacyContactEmail) },
        { label: '版', value: LEGAL_DOCUMENT_VERSIONS.data_access },
      ],
    },
  ];
}

/* ------------------------------------------------------------------ 組み立て */

const BUILDERS: Readonly<Record<LegalDocumentSlug, (p: LegalIssuerProfile) => LegalBlock[]>> = {
  terms: termsDocument,
  privacy: privacyDocument,
  aup: aupDocument,
  sla: slaDocument,
  tokushoho: tokushohoDocument,
  subcontractors: subcontractorsDocument,
  data_access: dataAccessDocument,
};

/** 文書を組み立てる。**公開可否の判定は呼び出し側**（`isLegalDocumentPublishable`）。 */
export function renderLegalDocument(
  slug: LegalDocumentSlug,
  profile: LegalIssuerProfile,
  at: Date = new Date(),
): LegalDocument {
  return {
    slug,
    title: LEGAL_DOCUMENT_TITLES_JA[slug],
    version: LEGAL_DOCUMENT_VERSIONS[slug],
    draft: profile.draft,
    effectiveFrom: LEGAL_DOCUMENT_SCHEDULE[slug].effectiveFrom,
    currentVersion: effectiveLegalVersion(slug, at),
    blocks: BUILDERS[slug](profile),
  };
}

/**
 * ハッシュ対象の正規化テキスト。
 *
 * **表示（HTML）ではなく、この関数の出力をハッシュする。** 画面のマークアップは
 * UI の都合で変わるが、それで同意の証跡が無効になっては困る。逆に、
 * 条文が 1 文字でも変われば必ず違う値になる。
 */
export function legalDocumentText(doc: LegalDocument): string {
  const lines: string[] = [`${doc.slug}\t${doc.version}\t${doc.title}\tdraft=${doc.draft ? '1' : '0'}`];
  for (const block of doc.blocks) {
    switch (block.kind) {
      case 'section':
        lines.push(`S\t${block.number}\t${block.heading}`);
        break;
      case 'paragraph':
        lines.push(`P\t${block.text}`);
        break;
      case 'list':
        for (const item of block.items) lines.push(`L\t${block.ordered ? 'o' : 'u'}\t${item}`);
        break;
      case 'table':
        for (const row of block.rows) lines.push(`T\t${row.label}\t${row.value}`);
        break;
      case 'notice':
        lines.push(`N\t${block.tone}\t${block.text}`);
        break;
    }
  }
  // 末尾に改行を付けて「最後の行が途中で切れていない」ことを表現する。
  return lines.join('\n') + '\n';
}
